import { useMutation, useQueryClient } from '@tanstack/react-query';

import { setDefaultAiProvider } from '@/lib/api/generated/api';

import { toAiProviderError } from '../errors';
import { aiProviderKeys } from '../queryKeys';
import type { AiProvider } from '../types';

/**
 * Mark one AI Provider as the organization's default (spec FR-009a).
 *
 * The backend flips the previous default `false` and the new one `true`
 * atomically in one transaction and answers with `204 No Content` — this
 * hook applies that same shape optimistically to the cached list so the
 * badge moves immediately, with `onError` rollback to the snapshot taken in
 * `onMutate`.
 */
export function useSetDefaultAiProvider() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      const response = await setDefaultAiProvider(id);
      if (response.status === 204) return;
      throw toAiProviderError(response.status, response.data);
    },
    onMutate: async (id: string) => {
      await queryClient.cancelQueries({ queryKey: aiProviderKeys.list() });
      const previous = queryClient.getQueryData<AiProvider[]>(
        aiProviderKeys.list(),
      );
      if (previous) {
        queryClient.setQueryData<AiProvider[]>(
          aiProviderKeys.list(),
          previous.map((provider) => ({
            ...provider,
            is_default: provider.id === id,
          })),
        );
      }
      return { previous };
    },
    onError: (_error, _id, context) => {
      if (context?.previous) {
        queryClient.setQueryData(aiProviderKeys.list(), context.previous);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: aiProviderKeys.all() });
    },
  });
}
